import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useApp, ADMIN_EMAIL } from "../../contexts/AppContext";
import GlobalSearch from "./GlobalSearch";

const logoUrl = "https://public.readdy.ai/ai/img_res/195817ff-c016-4391-b195-c404bb90dcaf.png";

const consumerLinks = [
  { label: "トップ", href: "/" },
  { label: "事業者を探す", href: "/providers" },
  { label: "カテゴリ", href: "/categories" },
  { label: "講座一覧", href: "/courses" },
  { label: "教材一覧", href: "/materials" },
  { label: "メッセージ", href: "/messages" },
];

const providerLinks = [
  { label: "トップ", href: "/" },
  { label: "マイショップ", href: "/mypage/provider" },
  { label: "出品する", href: "/sell" },
  { label: "事業者を探す", href: "/providers" },
];

const adminLinks = [
  { label: "トップ", href: "/" },
  { label: "管理ダッシュボード", href: "/admin" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showModeMenu, setShowModeMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const modeRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoggedIn, currentUser, mode, setMode, logout, isAdminMode } = useApp();

  const isAdmin = currentUser?.email === ADMIN_EMAIL;
  const navLinks = mode === "admin" ? adminLinks : mode === "provider" ? providerLinks : consumerLinks;
  const isProviderMode = mode === "provider";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setShowModeMenu(false);
    setShowUserMenu(false);
  }, [location]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (modeRef.current && !modeRef.current.contains(e.target as Node)) setShowModeMenu(false);
      if (userRef.current && !userRef.current.contains(e.target as Node)) setShowUserMenu(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleLogout = () => { logout(); navigate("/"); };

  const handleModeSelect = (m: "consumer" | "provider" | "admin") => {
    setMode(m);
    setShowModeMenu(false);
    if (m === "admin") navigate("/admin");
    else if (m === "provider") navigate("/mypage/provider");
  };

  const getModeLabel = () => {
    if (mode === "admin") return "管理者モード";
    if (mode === "provider") return "事業者モード";
    return "受講者モード";
  };
  const getModeColor = () => {
    if (mode === "admin") return "bg-deep-green";
    if (mode === "provider") return "bg-gold";
    return "bg-teal";
  };

  const headerBg = isProviderMode
    ? scrolled ? "bg-[#1a2744]/95 backdrop-blur-md border-b border-white/10" : "bg-transparent"
    : mode === "admin"
    ? scrolled ? "bg-deep-green/95 backdrop-blur-md border-b border-white/10" : "bg-transparent"
    : scrolled ? "bg-glass border-b border-gold/20" : "bg-transparent";

  const isDark = (isProviderMode || mode === "admin") && !scrolled;
  const logoFilter = isDark ? "brightness-0 invert" : "";

  return (
    <>
      <header className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${headerBg}`}>
        <div className="max-w-screen-xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <img src={logoUrl} alt="TSUGITE" className={`h-9 w-auto object-contain transition-all duration-300 ${logoFilter}`} />
            <div className="hidden sm:flex flex-col">
              <span className={`font-display text-xs italic font-light leading-none tracking-widest transition-colors ${isDark ? "text-white/60" : "text-deep-green"}`}>
                Artisan Knowledge Platform
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`nav-link text-sm font-sans transition-colors ${
                  location.pathname === link.href.split("?")[0]
                    ? mode === "admin" || isProviderMode ? "text-gold" : "active text-teal"
                    : isDark ? "text-white/80 hover:text-white" : "text-charcoal/70 hover:text-charcoal"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Search button */}
          <button
            onClick={() => setShowSearch(true)}
            className={`hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-sans cursor-pointer transition-all duration-200 ${isDark ? "border-white/20 text-white/60 hover:text-white hover:border-white/40" : "border-stone-200 text-charcoal/50 hover:text-charcoal hover:border-teal"}`}
          >
            <i className="ri-search-line text-sm" />
            <span className="hidden lg:inline">検索...</span>
          </button>

          {/* Right Actions */}
          <div className="hidden md:flex items-center gap-2">
            {isLoggedIn && (
              <div className="relative" ref={modeRef}>
                <button
                  onClick={() => setShowModeMenu(!showModeMenu)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-sans cursor-pointer transition-all duration-300 ${
                    mode === "admin" ? "border-gold/60 text-gold hover:border-gold bg-white/5"
                      : isProviderMode ? "border-gold/50 text-gold hover:border-gold bg-white/5"
                      : "border-deep-green/30 text-charcoal hover:border-teal"
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full ${getModeColor()}`} />
                  {getModeLabel()}
                  <i className="ri-arrow-down-s-line text-sm" />
                </button>
                {showModeMenu && (
                  <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-2xl border border-stone-200 overflow-hidden z-50">
                    <div className="px-3 py-2 border-b border-stone-100">
                      <p className="text-xs text-charcoal/40 font-sans">モードを切り替える</p>
                    </div>
                    {(["consumer", "provider"] as const).map((m) => (
                      <button key={m} onClick={() => handleModeSelect(m)}
                        className={`w-full px-4 py-3 text-left text-sm font-sans text-charcoal hover:bg-ivory transition-colors cursor-pointer flex items-center gap-2 ${mode === m ? (m === "consumer" ? "bg-teal/5" : "bg-gold/5") : ""}`}>
                        <span className={`w-2 h-2 rounded-full ${m === "consumer" ? "bg-teal" : "bg-gold"}`} />
                        {m === "consumer" ? "受講者モード" : "事業者モード"}
                        {mode === m && <i className={`ri-check-line ${m === "consumer" ? "text-teal" : "text-gold"} ml-auto text-base`} />}
                      </button>
                    ))}
                    {isAdmin && (
                      <button onClick={() => handleModeSelect("admin")}
                        className={`w-full px-4 py-3 text-left text-sm font-sans text-charcoal hover:bg-ivory transition-colors cursor-pointer flex items-center gap-2 border-t border-stone-100 ${mode === "admin" ? "bg-deep-green/5" : ""}`}>
                        <span className="w-2 h-2 rounded-full bg-deep-green" />管理者モード
                        {mode === "admin" && <i className="ri-check-line text-deep-green ml-auto text-base" />}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {isLoggedIn && currentUser ? (
              <div className="relative" ref={userRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-sans cursor-pointer transition-all duration-200 ${isDark ? "bg-white/10 text-white hover:bg-white/20" : "bg-deep-green/8 text-charcoal hover:bg-deep-green/12"}`}
                >
                  <div className="w-6 h-6 rounded-full bg-teal/30 flex items-center justify-center">
                    <i className="ri-user-line text-xs text-teal" />
                  </div>
                  <span className="max-w-[100px] truncate">@{currentUser.username || currentUser.name}</span>
                  <i className="ri-arrow-down-s-line text-sm" />
                </button>
                {showUserMenu && (
                  <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-2xl border border-stone-200 overflow-hidden z-50">
                    <div className="px-4 py-3 border-b border-stone-100">
                      <p className="text-sm font-sans text-charcoal font-medium">@{currentUser.username || currentUser.name}</p>
                      <p className="text-xs text-charcoal/40 font-sans">{currentUser.name}</p>
                    </div>
                    <Link to="/mypage" className="flex items-center gap-2 px-4 py-3 text-sm font-sans text-charcoal hover:bg-ivory cursor-pointer transition-colors">
                      <i className="ri-user-3-line text-teal" />マイページ
                    </Link>
                    {mode === "provider" && (
                      <Link to="/mypage/provider" className="flex items-center gap-2 px-4 py-3 text-sm font-sans text-charcoal hover:bg-ivory cursor-pointer transition-colors">
                        <i className="ri-store-2-line text-gold" />事業者ダッシュボード
                      </Link>
                    )}
                    {isAdminMode && (
                      <Link to="/admin" className="flex items-center gap-2 px-4 py-3 text-sm font-sans text-charcoal hover:bg-ivory cursor-pointer transition-colors">
                        <i className="ri-shield-user-line text-deep-green" />管理者画面
                      </Link>
                    )}
                    <div className="border-t border-stone-100">
                      <button onClick={handleLogout} className="w-full flex items-center gap-2 px-4 py-3 text-sm font-sans text-red-400 hover:bg-red-50 cursor-pointer transition-colors">
                        <i className="ri-logout-box-line" />ログアウト
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link to="/login" className={`text-sm font-sans transition-colors whitespace-nowrap cursor-pointer ${isDark ? "text-white/80 hover:text-white" : "text-charcoal hover:text-teal"}`}>
                  ログイン
                </Link>
                <Link to="/register" className={`text-xs px-4 py-2 rounded-full font-sans font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${isProviderMode ? "bg-gold text-white hover:bg-amber-500" : "btn-primary"}`}>
                  無料で出店する
                </Link>
              </>
            )}
          </div>

          {/* Mobile Hamburger */}
          <button
            className={`md:hidden w-9 h-9 flex items-center justify-center cursor-pointer transition-colors ${isDark ? "text-white" : "text-charcoal"}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="メニュー"
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
          </button>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className={`md:hidden border-t px-4 py-4 flex flex-col gap-3 ${isDark ? "bg-[#1a2744] border-white/10" : "bg-glass border-gold/20"}`}>
            {/* Mobile search */}
            <button
              onClick={() => { setMenuOpen(false); setShowSearch(true); }}
              className={`flex items-center gap-2 py-2.5 px-3 rounded-xl border cursor-pointer text-sm font-sans transition-colors ${isDark ? "border-white/20 text-white/70" : "border-stone-200 text-charcoal/60"}`}
            >
              <i className="ri-search-line" />講座・事業者・カテゴリを検索
            </button>
            {navLinks.map((link) => (
              <Link key={link.href} to={link.href}
                className={`text-sm py-2 border-b last:border-0 cursor-pointer font-sans ${isDark ? "text-white/80 border-white/10" : "text-charcoal border-stone-100"}`}>
                {link.label}
              </Link>
            ))}
            {isLoggedIn && (
              <div className="flex flex-col gap-1 mt-1">
                <button onClick={() => handleModeSelect("consumer")}
                  className={`w-full py-2.5 rounded-full border text-xs font-sans text-charcoal cursor-pointer whitespace-nowrap transition-colors ${mode === "consumer" ? "border-teal bg-teal/5 text-teal" : "border-stone-200 hover:border-teal"}`}>
                  <span className="w-2 h-2 rounded-full bg-teal inline-block mr-1.5" />受講者モード
                </button>
                <button onClick={() => handleModeSelect("provider")}
                  className={`w-full py-2.5 rounded-full border text-xs font-sans text-charcoal cursor-pointer whitespace-nowrap transition-colors ${mode === "provider" ? "border-gold bg-gold/5 text-gold" : "border-stone-200 hover:border-gold"}`}>
                  <span className="w-2 h-2 rounded-full bg-gold inline-block mr-1.5" />事業者モード
                </button>
                {isAdmin && (
                  <button onClick={() => handleModeSelect("admin")}
                    className={`w-full py-2.5 rounded-full border text-xs font-sans text-charcoal cursor-pointer whitespace-nowrap transition-colors ${mode === "admin" ? "border-deep-green bg-deep-green/5 text-deep-green" : "border-stone-200 hover:border-deep-green"}`}>
                    <span className="w-2 h-2 rounded-full bg-deep-green inline-block mr-1.5" />管理者モード
                  </button>
                )}
              </div>
            )}
            {isLoggedIn ? (
              <div className="flex gap-2">
                <Link to="/mypage" className="btn-outline text-xs px-4 py-2 flex-1 text-center">マイページ</Link>
                <button onClick={handleLogout} className="btn-outline text-xs px-4 py-2 flex-1 text-red-400 border-red-200 cursor-pointer whitespace-nowrap">ログアウト</button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Link to="/login" className="btn-outline text-xs px-4 py-2 flex-1 text-center">ログイン</Link>
                <Link to="/register" className="btn-primary text-xs px-4 py-2 flex-1 text-center">無料で出店する</Link>
              </div>
            )}
          </div>
        )}
      </header>
      {showSearch && <GlobalSearch onClose={() => setShowSearch(false)} />}
    </>
  );
}
